import { Icons } from '@shared/components/content/icons';
import { Button, type ButtonProps } from '@shared/components/ui/button';
import { cn } from '@shared/lib/utils';
import { useEffect, useRef, useState } from 'react';

interface CopyButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  onCopy: () => Promise<void> | void;
  label?: string;
  copiedLabel?: string;
  iconClassName?: string;
}

const RESET_MS = 2000;

export function CopyButton({
  onCopy,
  label = 'Copy',
  copiedLabel = 'Copied',
  iconClassName,
  className,
  ...buttonProps
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = async () => {
    try {
      await onCopy();
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), RESET_MS);
    } catch {
      // Parent's onCopy handler is responsible for surfacing errors.
    }
  };

  return (
    <Button
      type="button"
      onClick={handleClick}
      className={cn(className)}
      {...buttonProps}
    >
      {copied ? (
        <Icons.check className={cn('mr-1 size-3.5', iconClassName)} />
      ) : (
        <Icons.copy className={cn('mr-1 size-3.5', iconClassName)} />
      )}
      {copied ? copiedLabel : label}
    </Button>
  );
}
